import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, MessageSquare, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [formUrl, setFormUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [urlError, setUrlError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setUrlError('');
    setCreating(true);

    try {
      const response = await axios.post(
        `${API}/sessions`,
        { form_url: formUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Form session created!');
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create session';
      toast.error(errorMsg);
      setUrlError(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" aria-hidden="true" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />;
      default:
        return <MessageSquare className="h-5 w-5 text-[#475569]" aria-hidden="true" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Abandoned';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading your sessions...</p>
            <span className="sr-only">Loading dashboard content</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back, {user?.name}!
            </h1>
            <p className="text-[#475569]">Manage your form sessions and start new conversations</p>
          </div>

          {/* New Session Card */}
          {!showNewSession ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <Card 
                className="p-8 border-2 border-dashed border-[#E2E8F0] hover:border-[#1d4ed8] transition-colors" 
                data-testid="new-session-card"
              >
                <button
                  onClick={() => setShowNewSession(true)}
                  className="w-full flex flex-col items-center justify-center space-y-4 focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2 rounded-lg"
                  data-testid="start-new-session-button"
                  aria-label="Start a new form session"
                >
                  <div className="w-16 h-16 bg-[#1d4ed8] rounded-full flex items-center justify-center" aria-hidden="true">
                    <Plus className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-center">
                    <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Start New Form Session
                    </h2>
                    <p className="text-[#475569]">Paste a Google or Microsoft form link to begin</p>
                  </div>
                </button>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  New Form Session
                </h2>
                <form 
                  onSubmit={handleCreateSession} 
                  className="space-y-4" 
                  data-testid="create-session-form"
                  aria-label="Create new form session"
                >
                  <div>
                    <Label htmlFor="form-url" className="text-[#1e293b] font-medium">
                      Form URL <span className="text-red-600" aria-label="required">*</span>
                    </Label>
                    <Input
                      id="form-url"
                      data-testid="form-url-input"
                      type="url"
                      placeholder="https://docs.google.com/forms/..."
                      value={formUrl}
                      onChange={(e) => {
                        setFormUrl(e.target.value);
                        setUrlError('');
                      }}
                      required
                      className="text-base mt-1 focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                      aria-required="true"
                      aria-invalid={urlError ? 'true' : 'false'}
                      aria-describedby={urlError ? 'url-error' : 'url-hint'}
                    />
                    <p id="url-hint" className="mt-1 text-xs text-[#475569]">
                      Paste a link to a Google Forms or Microsoft Forms
                    </p>
                    {urlError && (
                      <p id="url-error" className="mt-1 text-sm text-red-600" role="alert">
                        {urlError}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      data-testid="create-session-button"
                      type="submit"
                      className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] btn-hover-lift focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                      disabled={creating}
                      aria-label={creating ? 'Creating session' : 'Start conversation with form'}
                    >
                      {creating ? 'Creating...' : 'Start Conversation'}
                    </Button>
                    <Button
                      data-testid="cancel-button"
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewSession(false);
                        setFormUrl('');
                        setUrlError('');
                      }}
                      className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                      aria-label="Cancel creating new session"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          )}

          {/* Sessions List */}
          <section aria-labelledby="sessions-heading">
            <h2 
              id="sessions-heading"
              className="text-2xl font-bold mb-6" 
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Your Form Sessions
            </h2>
            
            {sessions.length === 0 ? (
              <Card className="p-12 text-center border border-[#E2E8F0]" role="status">
                <MessageSquare className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
                <p className="text-[#475569]">No form sessions yet. Start your first one above!</p>
              </Card>
            ) : (
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                role="list"
                aria-label="List of your form sessions"
              >
                {sessions.map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    role="listitem"
                  >
                    <Card
                      className="p-6 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-all cursor-pointer h-full focus-within:ring-4 focus-within:ring-[#1d4ed8] focus-within:ring-offset-2"
                      data-testid={`session-card-${session.id}`}
                    >
                      <button
                        onClick={() => navigate(`/chat/${session.id}`)}
                        className="w-full text-left focus:outline-none"
                        aria-label={`Open ${session.form_title} form session, status: ${getStatusText(session.status)}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(session.status)}
                            <span className="text-sm font-medium">{getStatusText(session.status)}</span>
                          </div>
                        </div>
                        
                        <h3 className="font-bold text-lg mb-2 line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {session.form_title}
                        </h3>
                        
                        {session.form_description && (
                          <p className="text-sm text-[#475569] mb-4 line-clamp-2">
                            {session.form_description}
                          </p>
                        )}
                        
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                          <p className="text-xs text-[#475569]">
                            <time dateTime={session.created_at}>
                              Started {new Date(session.created_at).toLocaleDateString()}
                            </time>
                          </p>
                        </div>
                      </button>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
